import { useState, useEffect, useCallback } from "react";
import { stateOption } from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";

export const LivingDetails = () => {
	const { formValues, setFormValue, setFormError } = useFormDataStore();
	const { enabledFields, setFormErrors, setValidateFormDetails } = useFormStateStore();

	const { propertyBacked, state } = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		propertyBacked: false,
		state: false,
	});

	const handlePropertyChange = useCallback(
		(value: string) => {
			setFormValue("propertyBacked", value);
			setErrors((prev) => ({ ...prev, propertyBacked: !value }));
		},
		[setFormValue, setFormError, setErrors],
	);

	const handleStateChange = useCallback(
		(value: string) => {
			setFormValue("state", value);
			setErrors((prev) => ({ ...prev, state: !value }));
		},
		[setFormValue, setFormError, setErrors],
	);

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			propertyBacked: enabledFields.propertyBacked?.length == 0 ? false : !propertyBacked,
			state: !state,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm's Next Button can trigger this.
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [propertyBacked, state]);

	return (
		<div className="jF-space-y-4">
			{enabledFields.propertyBacked?.length != 0 && (
				<RadioGroupField
					id="radiopropertyBacked"
					label="Are you residential or commercial property backed?"
					value={propertyBacked}
					onChange={(value) => handlePropertyChange(value)}
					required
					error={errors.propertyBacked ? "This field is required" : ""}
					gridCols={2}
					className="jF-mt-4 jF-justify-center jF-w-full"
					options={enabledFields.propertyBacked
						.filter((s) => s.value)
						.map((s) => ({
							id: s.value,
							value: s.value,
							label: s.option,
						}))}
				/>
			)}

			{stateOption.length > 1 && (
				<RadioGroupField
					id="radioState"
					label="What is your current state of residence?"
					value={state || ""}
					onChange={(value) => handleStateChange(value)}
					required
					error={errors.state ? "This field is required" : ""}
					gridCols={4}
					showIcons={false}
					className="jF-mt-4 jF-justify-center jF-w-full"
					options={stateOption
						.filter((s) => s.value)
						.map((s) => ({
							id: s.value,
							value: s.value,
							label: s.option,
						}))}
				/>
			)}
		</div>
	);
};
